import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ShelterAdminNote {
  shelter_id: string;
  notes: string;
  follow_up_needed: boolean;
  updated_at: string;
  updated_by: string | null;
}

export const useShelterAdminNotes = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["shelter-admin-notes"],
    enabled,
    queryFn: async (): Promise<Record<string, ShelterAdminNote>> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return {};

      const { data, error } = await supabase
        .from("shelter_admin_notes")
        .select("*");

      if (error) throw new Error(error.message);

      const map: Record<string, ShelterAdminNote> = {};
      (data || []).forEach((row: any) => {
        map[row.shelter_id] = {
          shelter_id: row.shelter_id,
          notes: row.notes ?? "",
          follow_up_needed: row.follow_up_needed ?? false,
          updated_at: row.updated_at,
          updated_by: row.updated_by ?? null,
        };
      });
      return map;
    },
  });
};

export const useSaveShelterAdminNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shelterId, notes }: { shelterId: string; notes: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { error } = await supabase
        .from("shelter_admin_notes")
        .upsert(
          {
            shelter_id: shelterId,
            notes,
            updated_at: new Date().toISOString(),
            updated_by: user.id,
          },
          { onConflict: "shelter_id" },
        );

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-admin-notes"] });
    },
  });
};

export const useToggleShelterFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shelterId, followUp }: { shelterId: string; followUp: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { error } = await supabase
        .from("shelter_admin_notes")
        .upsert(
          {
            shelter_id: shelterId,
            follow_up_needed: followUp,
            updated_at: new Date().toISOString(),
            updated_by: user.id,
          },
          { onConflict: "shelter_id" },
        );

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-admin-notes"] });
    },
  });
};
